// frontend/src/pages/CamDoBrainCompetitors.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend } from 'recharts';
import camdobrainService from '../services/camdobrainService';
import Loading from '../components/Loading';
import './CamDoBrainCompetitors.css';

const CamDoBrainCompetitors = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const brandProfileIdParam = searchParams.get('brand_profile_id');

  // State
  const [loading, setLoading] = useState(true);
  const [brandProfiles, setBrandProfiles] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [contentGaps, setContentGaps] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [selectedTab, setSelectedTab] = useState('overview');
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load dashboard when brand changes
  useEffect(() => {
    if (selectedBrandId) {
      loadDashboard(selectedBrandId);
    }
  }, [selectedBrandId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const profilesRes = await camdobrainService.getBrandProfiles();
      const profiles = Array.isArray(profilesRes.data)
        ? profilesRes.data
        : (profilesRes.data?.results || []);
      setBrandProfiles(profiles);

      if (brandProfileIdParam && profiles.find(p => p.id === parseInt(brandProfileIdParam))) {
        setSelectedBrandId(parseInt(brandProfileIdParam));
      } else if (profiles.length > 0) {
        setSelectedBrandId(profiles[0].id);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadDashboard = async (brandId) => {
    try {
      const [dashboardRes, gapsRes] = await Promise.all([
        camdobrainService.getCompetitorDashboard(brandId),
        camdobrainService.getContentGaps(brandId)
      ]);

      setDashboardData(dashboardRes.data);
      setContentGaps(gapsRes.data?.gaps || []);
      setError(null);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setDashboardData(null);
    }
  };

  const handleGenerateComparison = async () => {
    try {
      setGenerating(true);
      await camdobrainService.generateCompetitorComparison(selectedBrandId);
      setSuccessMessage('Comparacion generada correctamente');
      loadDashboard(selectedBrandId);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error generating comparison:', err);
      setError(err.response?.data?.error || 'Error al generar comparacion');
    } finally {
      setGenerating(false);
    }
  };

  const getRankColor = (rank, total) => {
    if (rank === 1) return '#10b981';
    if (rank === total) return '#ef4444';
    return '#f59e0b';
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return '-';
    return `${value.toFixed(2)}%`;
  };

  // Prepare radar chart data
  const getRadarData = () => {
    if (!dashboardData?.latest_comparison) return [];

    const comparison = dashboardData.latest_comparison;
    return [
      {
        metric: 'Seguidores',
        Tu: comparison.own_followers || 0,
        Competencia: comparison.avg_competitor_followers || 0,
      },
      {
        metric: 'Engagement',
        Tu: (comparison.own_engagement_rate || 0) * 100,
        Competencia: (comparison.avg_competitor_engagement || 0) * 100,
      },
      {
        metric: 'Posts/Sem',
        Tu: comparison.own_posts_per_week || 0,
        Competencia: comparison.avg_competitor_posts_per_week || 0,
      },
      {
        metric: 'Crecimiento',
        Tu: comparison.own_growth_rate || 0,
        Competencia: comparison.avg_competitor_growth || 0,
      },
    ];
  };

  // Prepare bar chart data for metric comparison
  const getBarChartData = () => {
    if (!dashboardData?.latest_comparison) return [];

    const comparison = dashboardData.latest_comparison;
    return [
      {
        name: 'Seguidores',
        Tu: comparison.own_followers || 0,
        Promedio: comparison.avg_competitor_followers || 0,
      },
      {
        name: 'Engagement %',
        Tu: (comparison.own_engagement_rate || 0) * 100,
        Promedio: (comparison.avg_competitor_engagement || 0) * 100,
      },
      {
        name: 'Posts/Semana',
        Tu: comparison.own_posts_per_week || 0,
        Promedio: comparison.avg_competitor_posts_per_week || 0,
      },
    ];
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="competitors-page">
      {/* Header */}
      <div className="competitors-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/camdobrain')}>
            ← Volver
          </button>
          <h1>
            <span className="icon">🔍</span>
            Analisis de Competencia
          </h1>
        </div>
        <div className="header-actions">
          <select
            value={selectedBrandId || ''}
            onChange={(e) => setSelectedBrandId(Number(e.target.value))}
            className="brand-select"
          >
            {brandProfiles.map(brand => (
              <option key={brand.id} value={brand.id}>
                {brand.brand_name}
              </option>
            ))}
          </select>
          <button
            className="generate-btn"
            onClick={handleGenerateComparison}
            disabled={generating}
          >
            {generating ? '...' : '🔄 Nueva Comparacion'}
          </button>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="success-banner">
          {successMessage}
          <button onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs-container">
        <button
          className={`tab-btn ${selectedTab === 'overview' ? 'active' : ''}`}
          onClick={() => setSelectedTab('overview')}
        >
          📊 Vista General
        </button>
        <button
          className={`tab-btn ${selectedTab === 'metrics' ? 'active' : ''}`}
          onClick={() => setSelectedTab('metrics')}
        >
          📈 Metricas
        </button>
        <button
          className={`tab-btn ${selectedTab === 'gaps' ? 'active' : ''}`}
          onClick={() => setSelectedTab('gaps')}
        >
          🎯 Oportunidades
        </button>
      </div>

      {/* No Data State */}
      {!dashboardData?.latest_comparison && (
        <div className="no-data">
          <div className="icon">🔍</div>
          <h2>Sin datos de comparacion</h2>
          <p>Genera una nueva comparacion para ver como te posicionas frente a tu competencia.</p>
          <button onClick={handleGenerateComparison} disabled={generating}>
            {generating ? 'Generando...' : 'Generar Comparacion'}
          </button>
        </div>
      )}

      {dashboardData?.latest_comparison && (
        <>
          {/* Overview Tab */}
          {selectedTab === 'overview' && (
            <div className="overview-tab">
              {/* Rankings */}
              <div className="rankings-section">
                <h2>Tu Posicion</h2>
                <div className="rankings-grid">
                  {dashboardData.latest_comparison.rankings && Object.entries(dashboardData.latest_comparison.rankings).map(([metric, data]) => (
                    <div key={metric} className="ranking-card">
                      <div className="ranking-metric">{metric.replace(/_/g, ' ')}</div>
                      <div
                        className="ranking-position"
                        style={{ color: getRankColor(data.rank, data.total) }}
                      >
                        {getRankIcon(data.rank)}
                      </div>
                      <div className="ranking-info">
                        de {data.total} cuentas
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Comparison Cards */}
              <div className="comparison-cards">
                <div className="comparison-card you">
                  <div className="card-label">Tu Cuenta</div>
                  <div className="card-metrics">
                    <div className="metric">
                      <span className="metric-value">{formatNumber(dashboardData.latest_comparison.own_followers)}</span>
                      <span className="metric-label">Seguidores</span>
                    </div>
                    <div className="metric">
                      <span className="metric-value">{formatPercent(dashboardData.latest_comparison.own_engagement_rate)}</span>
                      <span className="metric-label">Engagement</span>
                    </div>
                    <div className="metric">
                      <span className="metric-value">{dashboardData.latest_comparison.own_posts_per_week || 0}</span>
                      <span className="metric-label">Posts/Semana</span>
                    </div>
                  </div>
                </div>

                <div className="vs-badge">VS</div>

                <div className="comparison-card competitors">
                  <div className="card-label">Promedio Competencia</div>
                  <div className="card-metrics">
                    <div className="metric">
                      <span className="metric-value">{formatNumber(dashboardData.latest_comparison.avg_competitor_followers)}</span>
                      <span className="metric-label">Seguidores</span>
                    </div>
                    <div className="metric">
                      <span className="metric-value">{formatPercent(dashboardData.latest_comparison.avg_competitor_engagement)}</span>
                      <span className="metric-label">Engagement</span>
                    </div>
                    <div className="metric">
                      <span className="metric-value">{dashboardData.latest_comparison.avg_competitor_posts_per_week || 0}</span>
                      <span className="metric-label">Posts/Semana</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Insights */}
              {dashboardData.latest_comparison.insights?.length > 0 && (
                <div className="insights-section">
                  <h2>Insights IA</h2>
                  <div className="insights-list">
                    {dashboardData.latest_comparison.insights.map((insight, idx) => (
                      <div key={idx} className="insight-card">
                        <div className="insight-icon">💡</div>
                        <p>{insight}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Metrics Tab */}
          {selectedTab === 'metrics' && (
            <div className="metrics-tab">
              {/* Bar Chart */}
              <div className="chart-section">
                <h2>Comparacion de Metricas</h2>
                <div className="chart-container bar-chart">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={getBarChartData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                      <Tooltip
                        contentStyle={{
                          background: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '0.85rem'
                        }}
                      />
                      <Legend />
                      <Bar dataKey="Tu" fill="#8b5cf6" name="Tu Cuenta" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Promedio" fill="#94a3b8" name="Promedio Competencia" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Radar Chart */}
              <div className="chart-section">
                <h2>Perfil Competitivo</h2>
                <div className="chart-container radar-chart">
                  <ResponsiveContainer width="100%" height={350}>
                    <RadarChart data={getRadarData()}>
                      <PolarGrid stroke="#e2e8f0" />
                      <PolarAngleAxis dataKey="metric" tick={{ fontSize: 12, fill: '#64748b' }} />
                      <PolarRadiusAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
                      <Radar name="Tu" dataKey="Tu" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.3} />
                      <Radar name="Competencia" dataKey="Competencia" stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.3} />
                      <Legend />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* History */}
              {dashboardData.comparison_history?.length > 1 && (
                <div className="history-section">
                  <h2>Historial de Comparaciones</h2>
                  <div className="history-list">
                    {dashboardData.comparison_history.map((comp, idx) => (
                      <div key={idx} className="history-item">
                        <span className="history-date">
                          {new Date(comp.comparison_date).toLocaleDateString('es-CL', {
                            day: 'numeric',
                            month: 'short'
                          })}
                        </span>
                        <span className="history-metric">
                          Engagement: {formatPercent(comp.own_engagement_rate)}
                        </span>
                        <span className={`history-diff ${comp.own_engagement_rate > comp.avg_competitor_engagement ? 'positive' : 'negative'}`}>
                          {comp.own_engagement_rate > comp.avg_competitor_engagement ? '↑' : '↓'}
                          vs competencia
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Gaps Tab */}
          {selectedTab === 'gaps' && (
            <div className="gaps-tab">
              <h2>Oportunidades de Contenido</h2>
              {contentGaps.length === 0 ? (
                <div className="no-gaps">
                  <p>No se encontraron brechas de contenido significativas.</p>
                </div>
              ) : (
                <div className="gaps-grid">
                  {contentGaps.map((gap, idx) => (
                    <div key={idx} className="gap-card">
                      <div className="gap-header">
                        <span className="gap-type">{gap.type}</span>
                        <span className={`gap-priority priority-${gap.priority}`}>
                          {gap.priority === 'high' ? '🔴 Alta' : gap.priority === 'medium' ? '🟡 Media' : '🟢 Baja'}
                        </span>
                      </div>
                      <h4>{gap.title}</h4>
                      <p>{gap.description}</p>
                      {gap.recommendation && (
                        <div className="gap-recommendation">
                          <strong>Recomendacion:</strong> {gap.recommendation}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Competitor Top Posts */}
              {dashboardData.top_competitor_posts?.length > 0 && (
                <div className="top-posts-section">
                  <h2>Top Posts de Competidores</h2>
                  <div className="top-posts-grid">
                    {dashboardData.top_competitor_posts.map((post, idx) => (
                      <div key={idx} className="top-post-card">
                        <div className="post-header">
                          <span className="post-account">@{post.account_username}</span>
                          <span className="post-engagement">{formatPercent(post.engagement_rate)} engagement</span>
                        </div>
                        <p className="post-caption">{post.caption?.substring(0, 150)}...</p>
                        <div className="post-stats">
                          <span>❤️ {formatNumber(post.likes)}</span>
                          <span>💬 {formatNumber(post.comments)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Last Updated */}
      {dashboardData?.latest_comparison && (
        <div className="last-updated">
          Ultima actualizacion: {new Date(dashboardData.latest_comparison.comparison_date).toLocaleDateString('es-CL', {
            day: 'numeric',
            month: 'long',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      )}
    </div>
  );
};

export default CamDoBrainCompetitors;
